/**
 * Sprint 7b — reports and the manager KPI panel (ADR 0018).
 *
 * The aggregations run in SQL (supabase/migrations/20270308000003_reports.sql, SECURITY INVOKER, RLS
 * applies). This module holds the shared limits, the row shapes the console receives, and the pure helpers
 * that work on one vehicle's route in the browser (jump filter, stops and segments). Pure TypeScript.
 *
 * ADR 0003: reports only read. Nothing here (or anywhere else) can act on a vehicle.
 */
import type { AlarmType } from "./alarms.js";
import { UI_LOW_SOC } from "./battery-lift.js";

export const REPORT_LIMITS = Object.freeze({
  /** Longest date range of the usage, event and checklist reports (local days, inclusive). */
  maxDays: 92,
  /** Longest window of the route report. */
  routeMaxHours: 24,
  /** Rows per export file; above it the console asks for a narrower filter. */
  exportMaxRows: 50_000,
  /** Points the route report fetches (24 h at 1 s plus positions). */
  routeMaxPoints: 60_000,
  /** A telemetry row's state lasts until the next row, at most this long (SQL: 120 s). */
  gapCapS: 120,
  /** Implied speed above which a GNSS segment is a jump (SQL usage distance and route cleaning). */
  maxSpeedKmh: 40,
  /** Implied speed below which a segment is stationary jitter (SQL usage distance). */
  minSpeedKmh: 1,
  /** Segments longer than this are not counted as driven distance (SQL: 10 min). */
  maxSegmentS: 600,
  /** A route stop: stationary (speed < 1 km/h or ignition off) for at least this long. */
  stopMinS: 120,
  /** Below this state of charge a vehicle counts as "low" on the KPI panel: the battery card's amber level (ADR 0017). */
  lowSocPct: UI_LOW_SOC,
});

export const REPORT_KINDS = ["usage", "events", "route", "checklist", "heatmap", "spaghetti", "driving", "battery", "lift", "eco", "kys_fr11", "kys_fr12"] as const;
export type ReportKind = (typeof REPORT_KINDS)[number];

export const EXPORT_FORMATS = ["csv", "xlsx", "pdf"] as const;
export type ExportFormat = (typeof EXPORT_FORMATS)[number];

/**
 * Utilisation basis (owner decision 2026-10-18): the site's shifts, the site's working hours (default
 * Mon–Fri 08:00–18:00, ADR 0011) or the whole day; "auto" = shifts where the site has any, else working hours.
 */
export const USAGE_BASES = ["auto", "shifts", "work", "24h"] as const;
export type UsageBasis = (typeof USAGE_BASES)[number];
export type EffectiveBasis = Exclude<UsageBasis, "auto">;

export const KPI_PERIODS = ["today", "7d", "30d"] as const;
export type KpiPeriod = (typeof KPI_PERIODS)[number];

/** Event report filter groups (the alarm types that exist today; overspeed is Phase 2). */
export const EVENT_GROUPS = ["impact", "safety", "geofence", "checklist", "overload", "battery", "device_health"] as const;
export type EventGroup = (typeof EVENT_GROUPS)[number];
export const EVENT_GROUP_TYPES: Readonly<Record<EventGroup, readonly AlarmType[]>> = Object.freeze({
  impact: ["impact"],
  safety: ["unauthorized_start", "card_denied", "seat_violation", "belt_violation", "tamper", "power_loss"],
  geofence: ["geofence_enter", "geofence_exit", "geofence_dwell"],
  checklist: ["checklist_critical", "checklist_missed", "checklist_notes"],
  overload: ["overload"],
  battery: ["deep_discharge", "bat_overtemp", "battery_low"],
  // ADR 0027: "Cihaz sağlığı" — recorder storage and camera channels (never located).
  device_health: ["dvr_storage", "dvr_camera", "device_output_control"],
});

/** Alarm types of the selected groups (empty = every type). */
export function typesOfGroups(groups: readonly EventGroup[]): AlarmType[] {
  return [...new Set(groups.flatMap((g) => EVENT_GROUP_TYPES[g] ?? []))];
}

/* ---------------- row shapes (what the console receives) ---------------- */

/** One vehicle on one local day (report_usage). Durations in seconds; null lift fields = no lift module. */
export type UsageRow = {
  vehicleId: string;
  fleetNo: string;
  siteId: string;
  day: string;
  ignitionS: number;
  movingS: number;
  idleS: number;
  distanceM: number;
  liftCount: number | null;
  liftedKg: number | null;
  liftEstimated: boolean | null;
  operatorIds: string[];
  /** Names of the operators the user may see; `operatorsHidden` counts the others (KVKK). */
  operatorNames: string[];
  operatorsHidden: number;
  /** Card numbers that match no registered card ("Bilinmeyen kart"). */
  operatorsUnknown: number;
  /** The utilisation basis used for this row. */
  basis: EffectiveBasis;
  basisS: number;
  basisIgnitionS: number;
  /** % (0–100); null when the basis is 0 (e.g. a day without shifts). */
  utilisation: number | null;
};

/** One alarm (report_events). Operator fields are null when the user may not see the operator. */
export type EventRow = {
  id: string;
  occurredAt: string;
  type: AlarmType;
  severity: number;
  status: "open" | "acknowledged";
  vehicleId: string;
  fleetNo: string | null;
  siteId: string | null;
  siteName: string | null;
  operatorId: string | null;
  operatorName: string | null;
  offHours: boolean;
  delayed: boolean;
  timeUnknown: boolean;
  repeatCount: number;
  ackedAt: string | null;
  ackNote: string | null;
  geofenceId: string | null;
  geofenceName: string | null;
  shiftName: string | null;
};

/** One position of the route report. */
export type RoutePoint = {
  ts: string;
  lat: number;
  lon: number;
  speedKmh: number | null;
  ignition: boolean;
  src: string | null;
  acc: number | null;
  operatorId: string | null;
  operatorName: string | null;
};

/** One vehicle and shift name (report_checklist). Shift fields are null when the site has no shifts. */
export type ChecklistComplianceRow = {
  vehicleId: string;
  fleetNo: string;
  siteId: string;
  shiftName: string | null;
  hasShifts: boolean;
  shiftsUsed: number | null;
  shiftsChecked: number | null;
  missed: number | null;
  submissions: number;
  compliance: number | null;
  faultsOpened: number;
  faultsResolved: number;
  /** Mean time to resolve, hours (faults resolved in the range). */
  mttrH: number | null;
};

/** A report result: the rows, the time zone the local days were cut in, and whether the list was cut. */
export type ReportResult<R> = { rows: R[]; tz: string; truncated: boolean };

/* ---------------- KPI panel ---------------- */

export type KpiSeriesPoint = {
  day: string;
  utilisation: number | null;
  active: number;
  impacts: number;
  checklist: number | null;
  geofence: number;
  deepDischarges: number;
  avgSoc: number | null;
  overloads: number;
};

/** kpi_panel(): null tiles do not exist for the scope (no module / no right to read); null values = no data yet. */
export type KpiPanel = {
  tz: string;
  period: KpiPeriod;
  from: string;
  to: string;
  prevFrom: string;
  prevTo: string;
  vehicles: { total: number; withDevice: number; active: number; prevActive: number };
  utilisation: { value: number | null; prev: number | null; hours: number; basis: EffectiveBasis | "mixed" };
  impacts: { value: number; prev: number; per100h: number | null; prevPer100h: number | null };
  checklist: { value: number | null; prev: number | null; hasShifts: boolean; used: number; missed: number };
  faults: { openCritical: number; maintenanceVehicles: number };
  geofence: { value: number; prev: number; enter: number; exit: number; dwell: number };
  battery: { lowSoc: number; deepDischarges: number; prevDeepDischarges: number; avgSoc: number | null } | null;
  lift: { overloads: number; prev: number } | null;
  documents: { expiring: number; expired: number } | null;
  series: KpiSeriesPoint[];
};

/**
 * Change against the previous period in % (rounded to a whole number). null when there is nothing to compare
 * with (no previous value, or it was 0 — a jump from 0 has no meaningful percentage).
 */
export function pctChange(cur: number | null | undefined, prev: number | null | undefined): number | null {
  if (cur === null || cur === undefined || prev === null || prev === undefined || !Number.isFinite(cur) || !Number.isFinite(prev) || prev === 0) return null;
  return Math.round(((cur - prev) / Math.abs(prev)) * 100);
}

/* ---------------- dates ---------------- */

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Local days from..to inclusive; NaN for malformed input. */
export function daysInRange(from: string, to: string): number {
  if (!DAY_RE.test(from) || !DAY_RE.test(to)) return Number.NaN;
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return Number.NaN;
  // Engines roll impossible dates over (2026-02-30 -> 03-02): the day must survive a round trip.
  if (new Date(a).toISOString().slice(0, 10) !== from || new Date(b).toISOString().slice(0, 10) !== to) return Number.NaN;
  return Math.round((b - a) / 86_400_000) + 1;
}

export type RangeIssue = "invalid" | "reversed" | "too_long";

/** Same rule as fts_report_check: both dates, from <= to, at most `maxDays` days. */
export function checkReportRange(from: string, to: string, maxDays: number = REPORT_LIMITS.maxDays): RangeIssue | null {
  const n = daysInRange(from, to);
  if (Number.isNaN(n)) return "invalid";
  if (n < 1) return "reversed";
  if (n > maxDays) return "too_long";
  return null;
}

/** Same rule as report_route: end after start, at most 24 h. */
export function checkRouteWindow(fromIso: string, toIso: string): RangeIssue | null {
  const a = Date.parse(fromIso);
  const b = Date.parse(toIso);
  if (Number.isNaN(a) || Number.isNaN(b)) return "invalid";
  if (b <= a) return "reversed";
  if (b - a > REPORT_LIMITS.routeMaxHours * 3_600_000) return "too_long";
  return null;
}

/* ---------------- route ---------------- */

/** Haversine distance in metres (same as SQL fts_dist_m). */
export function routeDistanceM(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const r = (d: number) => (d * Math.PI) / 180;
  const h = Math.sin(r(lat2 - lat1) / 2) ** 2 + Math.cos(r(lat1)) * Math.cos(r(lat2)) * Math.sin(r(lon2 - lon1) / 2) ** 2;
  return 2 * 6371008.8 * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Drops GNSS jumps: a point is dropped when reaching it from the last KEPT point needs more than 40 km/h.
 * (A single bad fix is skipped; the track continues from the last good one.) Points must be in time order.
 */
export function cleanRoute(points: readonly RoutePoint[], maxSpeedKmh: number = REPORT_LIMITS.maxSpeedKmh): { points: RoutePoint[]; dropped: number } {
  // A jump after a long gap is judged against the gap (a vehicle may have driven while unseen).
  const jump = (a: RoutePoint, b: RoutePoint) => {
    const dt = (Date.parse(b.ts) - Date.parse(a.ts)) / 1000;
    const d = routeDistanceM(a.lat, a.lon, b.lat, b.lon);
    return dt <= 0 ? d > 50 : (d / dt) * 3.6 > maxSpeedKmh && d > 50;
  };
  const out: RoutePoint[] = [];
  let dropped = 0;
  points.forEach((p, i) => {
    let last = out[out.length - 1];
    if (last && jump(last, p)) {
      const next = points[i + 1];
      // The accepted point may be the bad one (e.g. the first fix of the window): when this point agrees with
      // the next one and the accepted point agrees with neither, drop the accepted point instead.
      if (next && !jump(p, next) && jump(last, next)) {
        out.pop();
        dropped++;
        last = out[out.length - 1];
        if (last && jump(last, p)) {
          dropped++;
          return;
        }
      } else {
        dropped++;
        return;
      }
    }
    out.push(p);
  });
  return { points: out, dropped };
}

export type RouteSegment = {
  kind: "drive" | "stop";
  from: string;
  to: string;
  durationS: number;
  distanceM: number;
  maxSpeedKmh: number | null;
  lat: number;
  lon: number;
  endLat: number;
  endLon: number;
  /** Operators seen in the segment (names the user may see). */
  operators: string[];
};

const isStill = (p: RoutePoint) => !p.ignition || (p.speedKmh ?? 0) < 1;

/**
 * Splits a cleaned route into drives and stops. A stop is a stationary run (speed < 1 km/h or ignition off)
 * lasting at least `stopMinS`; shorter pauses belong to the surrounding drive. A run lasts from its first
 * point until the next run's first point; the last run ends at the last point. Distance counts only
 * segments of at most 10 minutes (a longer silence is not driven distance).
 */
export function routeSegments(points: readonly RoutePoint[], stopMinS: number = REPORT_LIMITS.stopMinS): RouteSegment[] {
  if (points.length === 0) return [];
  type Run = { still: boolean; i0: number; i1: number };
  const runs: Run[] = [];
  points.forEach((p, i) => {
    const still = isStill(p);
    const cur = runs[runs.length - 1];
    if (cur && cur.still === still) cur.i1 = i;
    else runs.push({ still, i0: i, i1: i });
  });
  const endTs = (r: Run, k: number) => (k + 1 < runs.length ? points[runs[k + 1]!.i0]!.ts : points[r.i1]!.ts);
  // Short pauses become part of the drive.
  const kinds = runs.map((r, k) => ({ ...r, stop: r.still && (Date.parse(endTs(r, k)) - Date.parse(points[r.i0]!.ts)) / 1000 >= stopMinS }));
  const merged: { stop: boolean; i0: number; i1: number }[] = [];
  for (const r of kinds) {
    const last = merged[merged.length - 1];
    if (last && last.stop === r.stop) last.i1 = r.i1;
    else merged.push({ stop: r.stop, i0: r.i0, i1: r.i1 });
  }
  return merged.map((m, k) => {
    const first = points[m.i0]!;
    // A segment runs until the next one starts (its last point belongs to it too).
    const stopIdx = k + 1 < merged.length ? merged[k + 1]!.i0 : m.i1;
    const last = points[stopIdx]!;
    let dist = 0;
    let vmax: number | null = null;
    const ops = new Set<string>();
    for (let i = m.i0; i <= stopIdx; i++) {
      const p = points[i]!;
      if (i <= m.i1 && p.operatorName) ops.add(p.operatorName);
      if (i <= m.i1 && p.speedKmh !== null && (vmax === null || p.speedKmh > vmax)) vmax = p.speedKmh;
      if (i > m.i0 && !m.stop) {
        const q = points[i - 1]!;
        const dt = (Date.parse(p.ts) - Date.parse(q.ts)) / 1000;
        if (dt > 0 && dt <= REPORT_LIMITS.maxSegmentS) dist += routeDistanceM(q.lat, q.lon, p.lat, p.lon);
      }
    }
    return {
      kind: m.stop ? "stop" : "drive",
      from: first.ts,
      to: last.ts,
      durationS: Math.max(0, Math.round((Date.parse(last.ts) - Date.parse(first.ts)) / 1000)),
      distanceM: Math.round(dist),
      maxSpeedKmh: m.stop ? null : vmax,
      lat: first.lat,
      lon: first.lon,
      endLat: last.lat,
      endLon: last.lon,
      operators: [...ops].sort(),
    };
  });
}
